<template>
    <b-container v-if="wizardData" fluid>
        <b-row class="mt-3">
            <b-col>
                <b>{{ wizardData.name }} - Gallery</b>
            </b-col>
        </b-row>
        <hr />
        <b-row class="mt-3 no-gutters">
            <b-col cols="auto" class="">
                <responsive-button
                    class="mb-sm-0"
                    variant="primary"
                    size="md"
                    :icon="['fas', 'trash']"
                    :responsiveness="true"
                    @click="$bvModal.show(deleteAllPhotosModalId)"
                    >Delete All Photos</responsive-button
                >
            </b-col>
            <b-col cols="auto" class="ml-sm-0">
                <responsive-button
                    class="mb-sm-0"
                    variant="primary"
                    size="md"
                    :icon="['fas', 'file-image']"
                    :responsiveness="true"
                    :disabled="!canRegenerateThumbnails"
                    @click="regenerateThumbnails"
                    >Regenerate Thumbnails</responsive-button
                >
            </b-col>
            <b-col cols="auto" class="ml-sm-0">
                <responsive-dropdown
                    class="mb-sm-0 sort-dropdown"
                    :icon="['fas', 'sort-amount-down-alt']"
                    :items="sortItems"
                    variant="primary"
                    >Sorting</responsive-dropdown
                >
            </b-col>
            <b-col v-if="isPlaceMode" cols="auto" class="ml-sm-0">
                <responsive-button
                    class="mb-sm-0"
                    variant="primary"
                    size="md"
                    :icon="['fas', 'sort']"
                    :responsiveness="true"
                    @click="$bvModal.show(orderPlaceModalID)"
                    >Organize Places</responsive-button
                >
            </b-col>
            <b-col v-if="isPlaceMode" cols="auto" class="ml-sm-0">
                <responsive-button
                    class="mb-sm-0"
                    variant="primary"
                    size="md"
                    :icon="['fas', 'microchip']"
                    :responsiveness="true"
                    @click="AIAssignConfirm()"
                    >AI Assign</responsive-button
                >
            </b-col>
        </b-row>
        <b-row class="mt-1 mb-2">
            <b-col cols="auto" class="mr-auto"></b-col>
            <b-col cols="auto">
                <small>{{ thumbnailsRegenerationStatus }}</small>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <form-file
                    class="form-file"
                    ref="fileInputComponent"
                    accept="image/*,.heic,.heif"
                    :multiple="true"
                    :formatNames="formatNames"
                    @input="(values) => uploadPhotos(values)"
                    placeholder="Drag and drop your photos here or click to upload"
                    drop-placeholder="Drop your photos"
                    type="file"
                    size="lg"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col style="margin-left: -3px">
                <span v-for="(label, j) in photoPlaces" :key="label[0]">
                    <div class="label-title" v-if="isPlaceMode && label[1].length > 0">
                        {{ getDisplayLabel(label) }}
                    </div>
                    <draggable
                        v-if="label[1].length > 0"
                        class="photo-cards"
                        :class="label[0]"
                        v-model="label[1]"
                        :sort="true"
                        :scroll-sensitivity="100"
                        :force-fallback="true"
                        group="photos"
                        handle=".handle"
                        @move="(e) => onMove(e)"
                        @end="(e) => onDragEnd(e)"
                        :key="label[0]"
                        :data-place-id="label[2].id"
                        :data-place-label="label[0]"
                    >
                        <div class="gallery-image" v-for="(photo, i) in label[1]" :key="photo.title + i" :id="photo.id">
                            <div class="gallery-primary-label" v-if="photo.is_primary">Main Photo</div>
                            <div class="gallery-image-header">
                                <div class="gallery-image-dimensions" v-if="!photo.warning">
                                    {{ photo.dimensions }}
                                </div>
                                <div
                                    class="gallery-image-dimensions photo-warning"
                                    v-if="photo.warning"
                                    title="Your photos will have better results with the guests if they are 1024x768."
                                >
                                    {{ photo.dimensions }}
                                    <font-awesome-icon
                                        class="photo-icons"
                                        size="sm"
                                        :icon="['fas', 'exclamation-triangle']"
                                    />
                                </div>
                                <div
                                    class="gallery-icon"
                                    v-if="!photo.deleting && photo.finished"
                                    @click="$bvModal.show(deletePhotoModalId + photo.id)"
                                >
                                    <font-awesome-icon
                                        v-if="photo.finished && !(isPlaceMode && photo.is_primary)"
                                        class="photo-icons"
                                        title="Delete Photo"
                                        color="#0e47a1"
                                        size="sm"
                                        :icon="['fas', 'trash']"
                                    />
                                </div>
                                <div
                                    class="gallery-icon"
                                    v-if="!photo.deleting && photo.finished"
                                    @click="downloadPhoto(photo.id)"
                                >
                                    <font-awesome-icon
                                        v-if="photo.finished"
                                        class="photo-icons"
                                        title="Download Photo"
                                        color="#0e47a1"
                                        size="sm"
                                        :icon="['fas', 'download']"
                                    />
                                </div>
                                <div
                                    class="gallery-icon"
                                    v-if="!photo.is_primary && !photo.deleting && photo.finished"
                                    @click="setPrimary(photo.id)"
                                >
                                    <font-awesome-icon
                                        v-if="photo.finished"
                                        class="photo-icons"
                                        title="Set Primary"
                                        color="#0e47a1"
                                        size="sm"
                                        :icon="['fas', 'angle-double-up']"
                                    />
                                </div>
                                <div
                                    class="gallery-icon"
                                    v-if="false && isPlaceMode && photo.is_primary && !photo.deleting && photo.finished"
                                    @click="unsetPrimary(photo.id)"
                                >
                                    <font-awesome-icon
                                        v-if="photo.finished"
                                        class="photo-icons"
                                        title="Unset primary photo"
                                        color="#0e47a1"
                                        size="sm"
                                        :icon="['fas', 'angle-double-down']"
                                    />
                                </div>
                                <div
                                    class="gallery-icon"
                                    v-if="!photo.is_primary && !photo.deleting && photo.finished && isPlaceMode"
                                >
                                    <responsive-dropdown
                                        v-if="photo.finished"
                                        class="label-button"
                                        title="Change photo place"
                                        :icon="['fas', 'tag']"
                                        :items="placeOptions(photo)"
                                        variant="outline-primary"
                                        :no-caret="true"
                                        :no-label="true"
                                        :dropleft="true"
                                    />
                                </div>
                            </div>
                            <div
                                class="primary gallery-image-container"
                                :class="{ 'handle gallery-image-container-cursor': !(isPlaceMode && photo.is_primary) }"
                            >
                                <div class="progress gallery-image-progress" v-if="!photo.finished">
                                    <div class="progress-bar" :style="`width: ${photo.percent}%`"></div>
                                </div>
                                <img
                                    :src="photo.thumbnail"
                                    alt="image"
                                    width="200"
                                    height="150"
                                    onerror="this.src='/static/images/image-placeholder.png'"
                                    disabled="!photo.finished"
                                />
                                <small v-if="photo.place && false"
                                    ><a>{{ photo.place.name }}</a></small
                                >
                            </div>
                            <div class="gallery-image-text">
                                <a @click="handleEditPhotoTitle(photo)" :disabled="!photo.finished">
                                    <div class="truncate" v-if="photo.title.length" :title="photo.title">
                                        {{ photo.title }}
                                    </div>
                                    <span v-if="!photo.title.length">Add caption</span>
                                </a>
                            </div>
                            <modal
                                :id="deletePhotoModalId + photo.id"
                                @ok="deletePhoto(photo.id)"
                                title="Delete Photo"
                                ok-title="Delete"
                            >
                                Are you sure want to delete this photo?
                            </modal>
                            <modal
                                :id="editTitleModalId + photo.id"
                                @ok="setPhotoTitle(photo.id)"
                                title="Edit Caption"
                                ok-title="Save"
                            >
                                <form-input label="Caption:" v-model="caption" />
                            </modal>
                        </div>
                    </draggable>
                </span>
            </b-col>
        </b-row>
        <b-row class="mt-3">
            <b-col>
                <h6 class="expandable" @click="changeShowVideo">
                    <b>Video&nbsp;</b>
                    <font-awesome-icon v-if="!showVideo" :icon="['fas', 'circle-plus']" />
                    <font-awesome-icon v-if="showVideo" :icon="['fas', 'circle-minus']" />
                </h6>
                <hr />
            </b-col>
        </b-row>
        <b-row v-if="showVideo">
            <b-col>
                <form-input v-model="videos.video_code" floatingLabel="Video URL" @keydown.enter="saveVideosUrls" />
            </b-col>
        </b-row>
        <b-row class="mt-4">
            <b-col>
                <h6 class="expandable" @click="changeShowVirtualTour">
                    <b>Virtual Tour&nbsp;</b>
                    <font-awesome-icon v-if="!showVirtualTour" :icon="['fas', 'circle-plus']" />
                    <font-awesome-icon v-if="showVirtualTour" :icon="['fas', 'circle-minus']" />
                </h6>
                <hr />
            </b-col>
        </b-row>
        <b-row v-if="showVirtualTour">
            <b-col>
                <form-input
                    v-model="videos.virtual_tours_code"
                    floatingLabel="Virtual Tour URL"
                    @keydown.enter="saveVideosUrls"
                />
            </b-col>
        </b-row>
        <b-button class="mt-4" variant="primary" @click="saveVideosUrls"> Save </b-button>
        <modal :id="deleteAllPhotosModalId" @ok="deleteAllPhotos()" title="Delete All Photo" ok-title="Delete">
            Are you sure want to delete all photos? This action cannot be reversed!
        </modal>
        <modal id="modal-reorder-simple" title="Simple Sort" @ok="setGallerySort('SIMPLE')"
            >All images will now be displayed without a place. Current place assigment order will be lost.
            Continue?</modal
        >
        <modal id="modal-reorder-category" title="Place Sort" @ok="setGallerySort('PLACE')"
            >All images will now be displayed by place. Current display order will be overwritten. Continue?</modal
        >
        <order-places-modal
            :modal-id="orderPlaceModalID"
            @success-save="handleOrderPlacesModalSuccessSave"
            :propertyId="propertyId"
        />
        <modal id="auto-assign-modal" title="AI Assign" @ok="AIAssign()">
            AI will try to automatically assign uncategorized photos to places. Continue?
            <br /><br />
            <p>
                <form-checkbox v-model="createPlaceIfNotExists" 
                    >Create place if it does not exist?
                </form-checkbox>
            </p>
            <p>
                <form-checkbox v-model="unassignAndReassign" 
                    >Unassign and reassign all photos?
                </form-checkbox>
            </p>
        </modal>
    </b-container>
</template>

<script>
    import { saveAs } from 'file-saver';
    import { mapGetters } from 'vuex';
    import API from '../../../../api';
    import { VueDraggable } from 'vue-draggable-plus';
    import Modal from '../../../generic/modal/modal.vue';
    import FormFile from '../../../generic/form-file/form-file.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormCheckbox from '../../../generic/form-checkbox/form-checkbox.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../../generic/responsive-dropdown/responsive-dropdown.vue';
    import { removeScrollMixin } from '../../../../mixins/remove-scroll-mixin';
    import { formatResponseError } from '../../../../utils/api';
    import { RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT } from '../../../../endpoints';
    import { WebSocketClient } from '../../../../websocket/client';
    import OrderPlacesModal from './order-places-modal.vue';

    export default {
        name: 'Gallery',
        components: {
            draggable: VueDraggable,
            Modal,
            FormInput,
            FormFile,
            ResponsiveButton,
            ResponsiveDropdown,
            OrderPlacesModal,
            FormCheckbox,
        },
        mixins: [removeScrollMixin],
        inject: ['propertyId'],
        computed: {
            ...mapGetters('setupPropertyStore', [
                'wizardData',
                'gallery',
                'canRegenerateThumbnails',
                'thumbnailsRegenerationStatus',
            ]),
            ...mapGetters('header', ['isImpersonated']),
            photoPlacesOrder: {
                get() {
                    return this.$store.getters['setupPropertyStore/photoPlacesOrder'];
                },
                set(newValue) {
                    this.$store.commit('setupPropertyStore/setPhotoPlacesOrder', newValue);
                },
            },
            photoPlacesOptions: {
                get() {
                    return this.$store.getters['setupPropertyStore/photoPlacesOptions'];
                },
            },
            photoPlaces() {
                var photoPlaces = [];

                if (this.isPlaceMode) {
                    let primaryPhotoArray = [
                        ['Main Photo', this.photos.filter((photo) => photo.is_primary), { id: null }],
                    ];

                    let otherPhotosArray = this.photoPlacesOrder.map((place) => {
                        return [
                            place.name,
                            this.photos.filter(
                                (photo) => photo.place && photo.place.name === place.name && !photo.is_primary
                            ),
                            place,
                        ];
                    });

                    photoPlaces = primaryPhotoArray.concat(otherPhotosArray);

                    photoPlaces = photoPlaces.concat([
                        [
                            'Uncategorized',
                            this.photos.filter((photo) => photo.place === null && !photo.is_primary),
                            { id: null },
                        ],
                    ]);
                } else {
                    photoPlaces = [['Uncategorized', this.photos, { id: null }]];
                }
                return photoPlaces;
            },
            sortItems() {
                return [
                    {
                        icon: ['fas', 'sort-amount-down-alt'],
                        label: 'Simple',
                        modal: 'modal-reorder-simple',
                        disabled: !this.isPlaceMode,
                    },
                    {
                        icon: ['fas', 'sort-amount-down-alt'],
                        label: 'By Place',
                        modal: 'modal-reorder-category',
                        disabled: this.isPlaceMode,
                    },
                ];
            },
        },
        data() {
            return {
                busy: false,
                showVideo: false,
                showVirtualTour: false,
                deletePhotoModalId: 'delete-photo-modal-id',
                deleteAllPhotosModalId: 'delete-all-photos-modal-id',
                editTitleModalId: 'edit-title-modal-id',
                orderPlaceModalID: 'order-place-modal-id',
                caption: '',
                photos: [],
                videos: [],
                isPlaceMode: false,
                createPlaceIfNotExists: false,
                unassignAndReassign: false,

                //galleryWebsocketToken: generateWebsocketToken(),
                //galleryWebsocketClient: new WebSocketClient(),
            };
        },
        async mounted() {
            //this.galleryWebsocketClient.initialize({ token: this.galleryWebsocketToken });
            //this.galleryWebsocketClient.setMessageCallback(this.handleGalleryWebsocketMessage);
            await this.fetchGallery();
        },
        methods: {
            async fetchGallery() {
                try {
                    this.$parent.$emit('busy', true);
                    if (!this.gallery) {
                        const data = await this.$store.dispatch('setupPropertyStore/getGallery', {
                            propertyId: this.propertyId,
                        });
                        this.photos = data.photos;
                        this.videos = data.videos;
                        this.isPlaceMode = data.is_place_mode;
                        this.photoPlacesOrder = data.photo_places_order;
                    } else {
                        this.photos = this.gallery.photos;
                        this.videos = this.gallery.videos;
                        this.isPlaceMode = this.gallery.isPlaceMode;
                        this.photoPlacesOrder = this.gallery.photoPlacesOrder;
                    }
                } catch (e) {
                    console.log(e);
                    this.errorToast({ text: 'Could not load gallery.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async refetchPhotos() {
                try {
                    this.$parent.$emit('busy', true);
                    const data = await this.$store.dispatch('setupPropertyStore/getGallery', {
                        propertyId: this.propertyId,
                    });
                    this.photos = data.photos;
                    this.isPlaceMode = data.is_place_mode;
                    this.photoPlacesOrder = data.photo_places_order;
                } catch (e) {
                    this.errorToast({ text: 'Could not load photos.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            handleEditPhotoTitle(photo) {
                this.caption = photo.title;
                this.$bvModal.show(this.editTitleModalId + photo.id);
            },
            formatNames(files) {
                return files.length === 1 ? 'Uploading your photo' : `Uploading ${files.length} photos`;
            },
            async deletePhoto() {
                try {
                    await this.$store.dispatch('setupPropertyStore/deletePhoto', {
                        id: this.photo.id,
                    });
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.errorToast({ text: generic || 'Could not delete photo.' });
                }
            },
            onMove(evt) {
                if (this.isPlaceMode) {
                    if (evt.data.is_primary) {
                        return false;
                    }
                }
            },
            async onDragEnd(evt) {
                console.log(evt.to);
                const target = evt.data;
                let newOrder = null;
                let newPlace = null;
                let isPrimary = false;

                if (this.isPlaceMode) {
                    newOrder = evt.newIndex + 1;
                    if (evt.to.dataset.placeId !== undefined) {
                        newPlace = evt.to.dataset.placeId;
                    } else {
                        newPlace = null;
                        isPrimary = evt.to.dataset.placeLabel === 'Main Photo';
                    }
                } else {
                    newOrder = evt.newIndex + 1;
                    newPlace = null;
                    isPrimary = evt.newIndex === 0;
                }

                const params = {
                    photo_id: target.id,
                    order: newOrder,
                    place_id: newPlace,
                    is_primary: isPrimary,
                };

                console.log(params);

                try {
                    if (target?.id) {
                        await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}set-order/`, params);
                        this.refetchPhotos();
                    }
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not move photo.',
                    });
                }
            },
            async setPlace(id, newPlace) {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}set-place/`, {
                        photo_id: id,
                        place_id: newPlace,
                    });
                    this.refetchPhotos();
                } catch (e) {
                    this.errorToast({ text: 'Could not set place.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async setPrimary(photoId) {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}set-primary/`, {
                        photo_id: photoId,
                    });
                    this.refetchPhotos();
                } catch (e) {
                    this.errorToast({ text: 'Could not set photo as primary photo.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async unsetPrimary(photoId) {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}unset-primary/`, {
                        photo_id: photoId,
                    });
                    this.refetchPhotos();
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not unset primary photo.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async setPhotoTitle(photoId) {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}set-photo-title/`, {
                        photo_id: photoId,
                        title: this.caption,
                    });
                    this.refetchPhotos();
                } catch (e) {
                    this.errorToast({ text: 'Could not update caption.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async downloadPhoto(photoId) {
                try {
                    this.$parent.$emit('busy', true);
                    const response = await API.get(
                        `${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}download-photo/?photo_id=${photoId}`,
                        { responseType: 'blob' }
                    );
                    saveAs(response.data, this.getFilenameFromResponse(response));
                } catch (e) {
                    this.errorToast({ text: 'Could not download photo.' });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            getFilenameFromResponse(response) {
                return response.headers['content-disposition'].split(';')[1].split('=')[1].split('"')[1].trim();
            },
            async uploadPhotos(photos) {
                var setNewPrimary = false;
                if (this.gallery.photos.length === 0) {
                    setNewPrimary = true;
                }
                if (photos) {
                    this.$parent.$emit('busy', true);
                    await Promise.all(photos.map((photo) => this.uploadPhoto(photo)));
                    this.$refs.fileInputComponent.$refs.fileInput.reset();
                    if (setNewPrimary) {
                        const newPrimary = this.gallery.photos.find((photo) => photo.label === 'exterior');
                        if (newPrimary) {
                            this.setPrimary(newPrimary.id);
                        }
                    }
                    this.refetchPhotos();
                    this.$parent.$emit('busy', false);
                }
            },
            async uploadPhoto(photo) {
                try {
                    const formData = new FormData();
                    formData.append('photo', photo);
                    const { data } = await API.post(
                        `${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}upload-photo/`,
                        formData
                    );
                    this.$store.commit('setupPropertyStore/gotNewPhoto', data.photo);
                    this.successToast({
                        text: `Successfully uploaded photo:\n ${photo.name}`,
                    });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || `Could not upload photo: ${photo.name}.`,
                    });
                }
            },
            async deletePhoto(photoId) {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}delete-photo/`, {
                        photo_id: photoId,
                    });
                    this.successToast({ text: 'Photo successfully deleted.' });
                    this.refetchPhotos();
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not delete photo.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async setGallerySort(sort) {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}set-gallery-sort/`, {
                        sort: sort,
                        token: this.galleryWebsocketToken,
                    });

                    this.successToast({ text: 'Photos successfully sorted.' });
                    this.refetchPhotos();
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not change photo gallery sort.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async deleteAllPhotos() {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}delete-all-photos/`);
                    this.successToast({ text: 'Photos successfully deleted.' });
                    this.refetchPhotos();
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not delete photos.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async regenerateThumbnails() {
                try {
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}regenerate-thumbnails/`);
                    this.$store.commit('setupPropertyStore/gotCanRegenerate', false);
                    this.successToast({
                        text:
                            'Started thumbnail regeneration... ' +
                            'You can navigate away from this page and return any time to see the status.',
                    });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not regenerate thumbnails.',
                    });
                }
            },
            changeShowVideo() {
                this.showVideo = !this.showVideo;
            },
            changeShowVirtualTour() {
                this.showVirtualTour = !this.showVirtualTour;
            },
            async saveVideosUrls() {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}save/`, {
                        ...this.videos,
                    });
                    this.successToast({ text: 'Urls successfully saved.' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not save urls.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            getDisplayLabel(label) {
                return label[0]; // + ' - ' + label[2].id;
            },
            AIAssignConfirm() {
                this.createPlaceIfNotExists = false;
                this.unassignAndReassign = false;
                this.$bvModal.show('auto-assign-modal');
            },
            async AIAssign() {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}auto-assign-places/`, {
                        create_places: this.createPlaceIfNotExists,
                        reassign_all: this.unassignAndReassign,
                    });
                    this.refetchPhotos();
                    this.successToast({ text: 'Uncategorized photos successfully assigned.' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not assign photos.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            placeOptions(photo) {
                let placeOptions = this.photoPlacesOptions
                    .filter(
                        (item) => (!photo.place && item.id !== null) || (photo.place && item.label !== photo.place.name)
                    )
                    .map((item) => ({
                        label: item.id ? item.label : 'Uncategorized',
                        click: () => {
                            this.setPlace(photo.id, item.id);
                        },
                    }));
                return placeOptions;
            },
            handleGalleryWebsocketMessage(message) {
                this.$parent.$emit('busy', false);
                this.successToast({ text: 'Photos successfully sorted.' });
                this.refetchPhotos();
            },
            async handleOrderPlacesModalSuccessSave(payload) {
                try {
                    this.$parent.$emit('busy', true);
                    await API.post(`${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}save-places-order/`, {
                        order: payload,
                    });
                    this.successToast({ text: 'Places order successfully saved.' });
                    this.refetchPhotos();
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not save places order.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(.label-button button) {
        border: 0 !important;
        padding: 0 !important;
    }

    :deep(.label-button button:hover) {
        color: #0d47a1 !important;
        background-color: #fff !important;
    }

    :deep(.label-button button svg) {
        height: 12px !important;
        margin-bottom: 8px;
        margin-right: -8px;
    }

    //:deep(.dropdown-menu {
    //    z-index: 9999 !important;
    //}

    .photo-cards {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        //justify-content: center;
    }

    div.label-title {
        font-weight: bold;
        margin-left: 10px;
    }

    div.gallery-image {
        width: 253px;
        //height: 241px;
        margin: 10px;
        padding: 4px;
        margin-bottom: 20px;
        line-height: 1.42857143;
        background-color: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 10px;
        -webkit-transition: border 0.2s ease-in-out;
        -o-transition: border 0.2s ease-in-out;
        transition: border 0.2s ease-in-out;
        //-webkit-box-shadow: 0 1px 2px rgba(0, 0, 0, 0.075);
        //box-shadow: 0 1px 2px rgba(0, 0, 0, 0.075);
        position: relative;
        overflow: visible;
        //z-index: 100;
    }
    .gallery-primary-label {
        background-color: #fff;
        border-radius: 5px 5px 5px 5px;
        border: 1px solid #e0e0e0;
        color: #00b74a;
        font-size: 12px;
        font-weight: 700;
        left: 50%;
        line-height: 1;
        opacity: 1;
        padding: 5px;
        position: absolute;
        text-align: center;
        top: -10px;
        transform: translateX(-50%);
        transition: all 0.15s ease-in-out;
        z-index: 50;
    }
    div.gallery-image-header {
        display: block;
        min-height: 15px;
    }
    div.gallery-image-dimensions {
        margin: 5px;
        margin-left: 10px;
        text-align: left;
        float: left;
        font-size: 12px;
    }
    .photo-warning {
        color: #ffa900;
    }
    .gallery-icon {
        float: right;
        right: 10px;
        position: relative;
        top: 3px;
        cursor: pointer;
        margin-left: 10px;
    }
    div.gallery-image-container {
        border: 1px solid #e0e0e0;
        padding: 10px;
        margin: 10px;
        background-color: #fff;
        border-radius: 8px;
    }
    div.gallery-image-container-cursor {
        cursor: move;
    }
    div.gallery-image-text {
        margin: 10px;
        cursor: pointer;
        color: #0e47a1;
        font-size: 13px;
    }
    .truncate {
        width: 210px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .expandable {
        cursor: pointer;
        &:hover {
            color: #0d47a1;
        }
    }

    :deep(.custom-file),
    :deep(.custom-file-input),
    :deep(.custom-file-label) {
        height: 10rem !important;
    }

    :deep(.custom-file-input) {
        border-radius: 8px;
    }

    :deep(.custom-file-label),
    :deep(.custom-file-label:after) {
        display: flex;
        justify-content: center;
        align-items: center;
        content: '' !important;
        padding: 30px !important;
        background-color: transparent !important;
        border-left: none !important;
        overflow: hidden;
    }

    :deep(.custom-file-label) {
        outline: 2px dashed #e0e0e0 !important;
        outline-offset: -15px !important;
        border: 1px solid #e0e0e0 !important;
    }
    .form-file {
        background-color: white !important;
        border-radius: 5px !important;
    }
    .photo-icons {
        margin-bottom: 4px;
    }
    @include media-breakpoint-down(md) {
        :deep(.sort-dropdown) {
            height: 37px;
        }
    }
</style>
